<template>
  <router-link
    data-test="card-link"
    :to="{ name: 'listing', params: { slug: listing.slug } }"
    class="group flex h-full flex-col overflow-hidden rounded-xl border border-boba-grey/50 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-btn-bg focus-visible:ring-offset-2 focus-visible:ring-offset-boba-sand"
  >
    <div
      class="relative aspect-[4/3] overflow-hidden border-b border-boba-grey/40 bg-boba-sand-light p-4 flex items-center justify-center"
    >
      <img
        v-if="image"
        :src="image"
        :alt="`${listing.title} product image`"
        loading="lazy"
        class="max-h-full max-w-full object-contain drop-shadow-sm transition-transform duration-300 group-hover:scale-[1.03]"
      />
      <div
        v-else
        data-test="image-placeholder"
        class="flex h-full w-full items-center justify-center"
      >
        <i class="fa-light fa-image text-5xl text-boba-grey" aria-hidden="true" />
      </div>

      <span
        data-test="status"
        class="absolute left-3 top-3 inline-flex items-center gap-x-1.5 rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide shadow-sm ring-1 ring-black/5"
        :class="status.classes"
      >
        <span
          v-if="phase === 'live'"
          class="relative flex h-2 w-2"
          aria-hidden="true"
        >
          <span
            class="absolute inline-flex h-full w-full animate-ping rounded-full bg-boba-green-dark opacity-75"
          />
          <span class="relative inline-flex h-2 w-2 rounded-full bg-boba-green-dark" />
        </span>{{ status.label }}</span
      >
    </div>

    <div class="flex flex-1 flex-col p-5">
      <p
        v-if="listing.subtitle"
        class="text-[11px] font-semibold uppercase tracking-[0.14em] text-boba-grey-dark"
      >
        {{ listing.subtitle }}
      </p>
      <h2
        class="mt-1 text-lg font-semibold leading-snug tracking-tight text-boba-brown text-balance"
      >
        {{ listing.title }}
      </h2>

      <p class="mt-2.5">
        <span
          class="inline-flex items-center gap-x-1.5 rounded-md border border-boba-grey/60 bg-boba-sand-light px-2 py-0.5 text-xs font-medium text-boba-grey-dark"
        >
          <i :class="typeIcon" class="text-[11px]" aria-hidden="true" />
          {{ listing.isLowBid() ? "Sealed bid" : "Falling price" }}
        </span>
      </p>

      <dl class="mt-4 space-y-1.5 text-sm">
        <div
          v-if="price"
          class="flex items-baseline justify-between gap-x-3"
        >
          <dt class="text-boba-grey-dark">{{ price.label }}</dt>
          <dd class="font-semibold tabular-nums text-boba-brown">
            {{ $format(price.amount / MILE) }}
            <span class="font-normal text-boba-grey-dark">{{
              price.amount === MILE ? "mile" : "miles"
            }}</span>
          </dd>
        </div>

        <div
          v-if="phase === 'ended'"
          class="flex items-baseline justify-between gap-x-3"
        >
          <dt class="text-boba-grey-dark">{{ endedOn ? "Ended" : "Opened" }}</dt>
          <dd data-test="ended-on" class="font-semibold tabular-nums text-boba-brown">
            {{ endedOn ?? openedOn }}
          </dd>
        </div>
        <countdown v-else-if="countdownTo" :to="countdownTo" v-slot="{ countdown }">
          <div class="flex items-baseline justify-between gap-x-3">
            <dt class="flex items-center gap-x-1.5 text-boba-grey-dark">
              <i class="fa-regular fa-clock text-xs" aria-hidden="true" />
              {{ countdown.ended ? "Closing" : countdownLabel }}
            </dt>
            <dd class="font-semibold tabular-nums text-boba-brown">
              {{ countdown.ended ? "now" : countdown.text }}
            </dd>
          </div>
        </countdown>
      </dl>

      <p class="flex-1" />

      <p
        data-test="cta"
        class="mt-5 block w-full rounded-lg py-2.5 text-center text-sm font-semibold transition-colors"
        :class="
          phase === 'live'
            ? 'bg-primary-btn-bg text-primary-btn-txt group-hover:bg-primary-btn-bg/90'
            : 'border border-boba-brown/20 text-boba-brown group-hover:border-boba-brown/40 group-hover:bg-boba-sand-light'
        "
      >
        {{ cta }}
      </p>
    </div>
  </router-link>
</template>
<script setup lang="ts">
import { computed } from "vue";
import Countdown from "@/components/Headless/Countdown.vue";
import { Listing } from "@/types";
import { MILE } from "@/util/miles";

const props = defineProps<{ listing: Listing }>();

type Phase = "live" | "upcoming" | "ended";

const phase = computed<Phase>(() => {
  if (props.listing.hasEnded()) {
    return "ended";
  }
  return props.listing.isActive() ? "live" : "upcoming";
});

const image = computed(() => props.listing.images[0] ?? null);

const typeIcon = computed(() =>
  props.listing.isLowBid()
    ? "fa-solid fa-envelope"
    : "fa-solid fa-arrow-trend-down",
);

const status = computed(() => {
  switch (phase.value) {
    case "live":
      return { label: "Live", classes: "bg-white text-boba-green-dark" };
    case "upcoming":
      return { label: "Upcoming", classes: "bg-white text-boba-grey-dark" };
    default:
      return { label: "Ended", classes: "bg-boba-brown text-white" };
  }
});

/** The one number worth surfacing on the card for this phase. */
const price = computed<{ label: string; amount: number } | null>(() => {
  const l = props.listing;

  if (l.isLowBid()) {
    if (phase.value === "ended") {
      return l.clearing_price != null
        ? { label: "Cleared at", amount: l.clearing_price }
        : null;
    }
    return l.hasPublicFloor()
      ? { label: "Bids from", amount: l.min_bid as number }
      : null;
  }

  if (phase.value === "live" && l.current_price) {
    return { label: "Current price", amount: l.current_price.price };
  }
  return null;
});

const countdownTo = computed(() => {
  if (phase.value === "upcoming") {
    return props.listing.startsAt();
  }
  return props.listing.countdownTarget();
});

const countdownLabel = computed(() => {
  if (phase.value === "upcoming") {
    return "Starts in";
  }
  return props.listing.isLowBid() ? "Ends in" : "Price drops in";
});

const DATE = "LLL d, yyyy";

/** Close date when the API records one (sealed bid); falling-price closes are not stored. */
const endedOn = computed(() => {
  const ends = props.listing.endsAt();
  return ends ? ends.toFormat(DATE) : null;
});

const openedOn = computed(() => props.listing.startsAt().toFormat(DATE));

const cta = computed(() => {
  switch (phase.value) {
    case "ended":
      return "View results";
    case "upcoming":
      return "Preview auction";
    default:
      return props.listing.isLowBid() ? "Place a bid" : "Buy now";
  }
});
</script>
